import * as yup from 'yup';
import {
  BarStatusType,
  BarType,
  CellarStatusType,
  CellarType,
  FobGasType,
  FobStatus,
  FobType,
  GlassWasherStatusType,
  type PreCleanRatingType,
  RemoteCoolerStatusType,
  RemoteCoolerType,
  TapStatusType,
  TapTaintProfileType,
  TapType,
  VisitStatus,
  type VisitTapParams,
  WastageStatusType,
  WeekDays
} from '../types';

export const createVisitDialogSchema = yup.object().shape({
  venueId: yup.string().required().label('Venue Id'),
  subscriptionId: yup.string().required().label('Subscription Id'),
  status: yup.mixed<VisitStatus>().oneOf(Object.values(VisitStatus)).required().label('Status'),
  users: yup.array().of(yup.string().required()).required().label('Technicians'),
  mainUserId: yup.string().required().label('Main technician'),
  startedAt: yup.string().required().label('Start date'),
  checkedOutAt: yup.string().label('End date')
});

export const updateVisitDialogSchema = yup.object().shape({
  visitId: yup.string().required().label('Visit Id'),
  venueId: yup.string().required().label('Venue Id'),
  subscriptionId: yup.string().required().label('Subscription Id'),
  status: yup.mixed<VisitStatus>().oneOf(Object.values(VisitStatus)).required().label('Status'),
  activeBars: yup.array().of(yup.string().required()).required().label('Bars'),
  users: yup.array().of(yup.string().required()).required().label('Technicians'),
  mainUserId: yup.string().required().label('Main technician'),
  startedAt: yup.string().required().label('Start date'),
  checkedOutAt: yup.string().label('End date')
});

export const updateVisitBarDialogSchema = yup.object().shape({
  id: yup.string().required().label('Id'),
  nozzleHygiene: yup
    .boolean()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional()
    .label('Nozzle Hygiene'),
  glasswareHygiene: yup
    .boolean()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional()
    .label('Glassware Hygiene'),
  tapHygiene: yup
    .boolean()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional()
    .label('Tap Hygiene'),
  flushWaterClear: yup.boolean().optional().label('Flush Water Clear'),
  barId: yup.string().required(),
  barName: yup.string().required(),
  barAccessDuringLicense: yup.boolean().required(),
  barStatusType: yup.mixed<BarStatusType>().oneOf(Object.values(BarStatusType)).required(),
  barBarType: yup
    .mixed<BarType>()
    .oneOf(Object.values(BarType))
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  barOpeningHour: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  barClosingHour: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  barClosedDay: yup
    .mixed<WeekDays>()
    .oneOf(Object.values(WeekDays))
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  barDescription: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  barAccessDays: yup
    .array()
    .of(yup.mixed<WeekDays>().oneOf(Object.values(WeekDays)).required())
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  barAccessWindowOpen: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  barAccessWindowClose: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  barProductDeliveryDays: yup
    .array()
    .of(yup.mixed<WeekDays>().oneOf(Object.values(WeekDays)).required())
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  isUnserviced: yup
    .boolean()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional()
});

export const updateVisitTapDialogSchema = yup.object<VisitTapParams>().shape({
  id: yup.string().required().label('Id'),
  preCleanRating: yup
    .mixed<PreCleanRatingType>()
    .optional()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .label('Pre Clean Rating'),
  wastageStatus: yup
    .string()
    .optional()
    .oneOf([...Object.values(WastageStatusType)])
    .label('Start Wastage Status'),
  hasBeerIn: yup.boolean().default(true).label('Beer in?'),
  manualWastage: yup
    .number()
    .optional()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .label('Manual Wastage'),
  totalWastage: yup
    .number()
    .optional()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .label('Total Wastage'),
  dispenseTemperature: yup
    .number()
    .optional()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .label('Dispense Temperature'),
  tapTaintProfile: yup
    .string()
    .optional()
    .oneOf([...Object.values(TapTaintProfileType), undefined])
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .label('Tap Taint Profile'),
  visitBarId: yup.string().required(),
  visitFobId: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  visitRemoteCoolerId: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  visitPythonId: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  tapId: yup.string().required(),
  tapPosition: yup.number().required(),
  tapStatus: yup.mixed<TapStatusType>().oneOf(Object.values(TapStatusType)).required(),
  tapType: yup.mixed<TapType>().oneOf(Object.values(TapType)).required(),
  tapTBarNumber: yup
    .number()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  tapLineLength: yup
    .number()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  tapBarId: yup.string().required(),
  tapFobId: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  tapProductId: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  tapRemoteCoolerId: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  tapPythonId: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  isUnserviced: yup
    .boolean()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional()
});

export const updateVisitGlasswasherDialogSchema = yup.object().shape({
  id: yup.string().required().label('Id'),
  hygieneCheck: yup.boolean().optional().label('Hygiene Check'),
  detergentCurrent: yup.number().optional().label('Detergent Current'),
  detergentNew: yup.number().optional().label('Detergent New'),
  rinseCurrent: yup.number().optional().label('Rinse Current'),
  rinseNew: yup.number().optional().label('Rinse New'),
  visitBarId: yup.string().required(),
  glassWasherId: yup.string().required(),
  glassWasherStatus: yup.mixed<GlassWasherStatusType>().oneOf(Object.values(GlassWasherStatusType)).required(),
  glassWasherPosition: yup.number().required(),
  glassWasherLocation: yup.string().required(),
  glassWasherDetergentPerLevel: yup
    .number()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  glassWasherRinsePerLevel: yup
    .number()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  glassWasherDescription: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  glassWasherBarId: yup.string().required(),
  isUnserviced: yup
    .boolean()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional()
});

export const updateVisitCellarDialogSchema = yup.object().shape({
  id: yup.string().required().label('Id'),
  temperature: yup.number().optional().label('Temperature'),
  pythonCondition: yup.boolean().optional().label('Python Condition'),
  couplerHygiene: yup.boolean().optional().label('Coupler Hygiene'),
  fobInternalHygiene: yup.boolean().optional().label('FOBs Internal Hygiene'),
  socketHygiene: yup.boolean().optional().label('Socket Hygiene'),
  backBoardHygiene: yup.boolean().optional().label('Back Board Hygiene'),
  caskEquipmentHygiene: yup.boolean().optional().label('Cask Equipment Hygiene'),
  cellarId: yup.string().required(),
  cellarName: yup.string().required(),
  cellarDescription: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  cellarStatus: yup.mixed<CellarStatusType>().oneOf(Object.values(CellarStatusType)).required(),
  cellarType: yup.mixed<CellarType>().oneOf(Object.values(CellarType)).required(),
  isUnserviced: yup
    .boolean()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional()
});

export const updateVisitRemoteDialogSchema = yup.object().shape({
  id: yup.string().required().label('Id'),
  temperature: yup.number().optional().label('Temperature'),
  iceBank: yup.boolean().optional().label('Ice Bank'),
  grilleHygiene: yup.boolean().optional().label('Grille Hygiene'),
  // Relations
  visitCellarId: yup.string().required(),
  // RemoteCooler Fields
  remoteCoolerId: yup.string().required(),
  remoteCoolerType: yup.mixed<RemoteCoolerType>().oneOf(Object.values(RemoteCoolerType)).required(),
  remoteCoolerStatus: yup.mixed<RemoteCoolerStatusType>().oneOf(Object.values(RemoteCoolerStatusType)).required(),
  remoteCoolerPosition: yup.number().required(),
  remoteCoolerTopPumpReturnRate: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  remoteCoolerDescription: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  remoteCoolerMaxLines: yup
    .number()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  remoteCoolerIsUnderCounter: yup
    .boolean()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  remoteCoolerLocation: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  remoteCoolerIsCaskDedicated: yup
    .boolean()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  // RemoteCooler Relations
  remoteCoolerCellarId: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  isUnserviced: yup
    .boolean()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional()
});

export const updateVisitFobDialogSchema = yup.object().shape({
  id: yup.string().required().label('Id'),
  topPressure: yup.number().optional().label('Top Pressure'),
  pumpPressure: yup.number().optional().label('Pump Pressure'),
  visitCellarId: yup.string().required(),
  // Fob data
  fobId: yup.string().required(),
  fobPosition: yup.number().required(),
  fobStatus: yup.mixed<FobStatus>().oneOf(Object.values(FobStatus)).required(),
  fobPump: yup.boolean().required(),
  maxLines: yup
    .number()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  fobType: yup
    .mixed<FobType>()
    .oneOf(Object.values(FobType))
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  fobGasType: yup
    .mixed<FobGasType>()
    .oneOf(Object.values(FobGasType))
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional(),
  // Fob Relations
  fobCellarId: yup.string().required(),
  fobProductId: yup
    .string()
    .transform((value, originalValue) => (originalValue === null ? undefined : value))
    .optional()
});
